import { ArrowLeft, Save, Play, Power, History, ChevronDown, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { WorkflowMeta, WorkflowStatus } from './types';

export interface WorkflowDesignerTopBarProps {
  workflow: WorkflowMeta;
  onWorkflowChange?: (updates: Partial<WorkflowMeta>) => void;
  onBack?: () => void;
  onSave?: () => void;
  onStatusChange?: (status: WorkflowStatus) => void;
  onRun?: () => void;
  onTest?: () => void;
  onOpenHistory?: () => void;
  className?: string;
}

const STATUS_STYLES: Record<WorkflowStatus, string> = {
  draft: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  active: 'bg-primary/15 text-primary',
  inactive: 'bg-muted text-muted-foreground',
  archived: 'bg-muted text-muted-foreground line-through',
};

const STATUS_LABELS: Record<WorkflowStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  inactive: 'Inactive',
  archived: 'Archived',
};

export function WorkflowDesignerTopBar({
  workflow,
  onWorkflowChange,
  onBack,
  onSave,
  onStatusChange,
  onRun,
  onTest,
  onOpenHistory,
  className,
}: WorkflowDesignerTopBarProps) {
  const status = workflow.status;
  const isActive = status === 'active';

  return (
    <div
      className={cn(
        'flex flex-none items-center gap-3 border-b bg-background px-4 py-2.5',
        className
      )}
    >
      {onBack && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onBack}
          aria-label="Back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <Input
          className="h-7 max-w-[280px] border-0 bg-transparent px-0 text-sm font-medium shadow-none focus-visible:ring-0"
          value={workflow.name}
          onChange={(e) => onWorkflowChange?.({ name: e.target.value })}
          placeholder="Workflow name"
        />
        <Input
          className="h-6 max-w-[420px] border-0 bg-transparent px-0 text-xs text-muted-foreground shadow-none focus-visible:ring-0"
          value={workflow.description}
          onChange={(e) => onWorkflowChange?.({ description: e.target.value })}
          placeholder="Add a description"
        />
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {workflow.version != null && (
          <span className="text-xs text-muted-foreground">v{workflow.version}</span>
        )}
        <Badge variant="secondary" className={cn('shrink-0 text-xs', STATUS_STYLES[status])}>
          {STATUS_LABELS[status]}
        </Badge>

        {onSave && (
          <Button variant="outline" size="sm" className="h-8 gap-1" onClick={onSave}>
            <Save className="h-3.5 w-3.5" />
            Save
          </Button>
        )}

        {onStatusChange && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={isActive ? 'outline' : 'default'} size="sm" className="h-8 gap-1">
                <Power className="h-3.5 w-3.5" />
                Status
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={isActive} onClick={() => onStatusChange('active')}>
                Activate
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={status === 'inactive'}
                onClick={() => onStatusChange('inactive')}
              >
                Deactivate
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={status === 'draft'}
                onClick={() => onStatusChange('draft')}
              >
                Move to draft
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={status === 'archived'}
                onClick={() => onStatusChange('archived')}
              >
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {onRun && isActive && (
          <Button variant="secondary" size="sm" className="h-8 gap-1" onClick={onRun}>
            <Zap className="h-3.5 w-3.5" />
            Run
          </Button>
        )}

        {onTest && (
          <Button variant="ghost" size="sm" className="h-8 gap-1" onClick={onTest}>
            <Play className="h-3.5 w-3.5" />
            Test
          </Button>
        )}

        {onOpenHistory && (
          <Button variant="ghost" size="sm" className="h-8 gap-1" onClick={onOpenHistory}>
            <History className="h-3.5 w-3.5" />
            History
          </Button>
        )}
      </div>
    </div>
  );
}
